import api from './api';
import { ApiResponse } from '../types';

// Wave 4.2d — legal & compliance policies customer API.

export interface EffectivePolicy {
  docType: string;
  title: string;
  contentHtml: string;
  version: number;
  effectiveDate: string | null;
  scope: 'platform' | 'merchant';
  ownerId: string | null;
  isCustomised: boolean;
  policyDocumentId: string;
  /**
   * Who the reader is contracting with — the SAME string the server substituted
   * for `[CompanyName]` in `contentHtml` (item B26).
   *
   * ⚠ Never re-derive this in the browser. The merchant-scoped legal page names
   * the party in its chrome, and the only thing that guarantees the chrome and
   * the sentence agree is that both came from this one value.
   */
  companyName: string;
}

/**
 * A merchant's policy as their EDITOR needs it (item B62): `contentHtml` is the
 * document as published; `editableHtml` is the stored text WITH its
 * placeholders. ⚠ Only `editableHtml` may go into the editor — saving
 * `contentHtml` writes the merchant's details in as literal text, and the
 * document never follows their settings again.
 */
export interface EditablePolicy extends EffectivePolicy {
  editableHtml: string;
}

export const policiesApi = {
  labels: () =>
    api.get<ApiResponse<{ labels: Record<string, string>; docTypes: string[] }>>('/policies/labels'),

  effective: (docType: string, merchantId?: string) =>
    api.get<ApiResponse<{ policy: EffectivePolicy }>>(
      `/policies/effective?docType=${encodeURIComponent(docType)}${merchantId ? `&merchantId=${merchantId}` : ''}`),

  /**
   * B111 — the same document as a PDF, rendered by the API from the resolved
   * text on every request (so it can never disagree with the page). An href,
   * not a call: the browser opens it in a new tab. Same-origin on both
   * hostnames, because host nginx routes `/api/` to the API on each.
   */
  effectivePdfHref: (docType: string, merchantId?: string): string =>
    `/api/policies/effective/pdf?docType=${encodeURIComponent(docType)}${merchantId ? `&merchantId=${encodeURIComponent(merchantId)}` : ''}`,

  listMine: () =>
    api.get<ApiResponse<{ policies: EditablePolicy[] }>>('/policies/merchant'),

  save: (docType: string, title: string, contentHtml: string) =>
    api.put<ApiResponse<{ policy: EditablePolicy }>>(`/policies/merchant/${docType}`, { title, contentHtml }),

  /** Back to the ReCurr template for one document (B62). */
  reset: (docType: string) =>
    api.delete<ApiResponse<{ policy: EditablePolicy }>>(`/policies/merchant/${docType}`),

  accept: (docTypes: string[], merchantOwnerId?: string | null) =>
    api.post<ApiResponse<{ recorded: number }>>('/policies/accept', { docTypes, merchantOwnerId }),

  pending: (merchantId?: string) =>
    api.get<ApiResponse<{ pending: string[]; labels: Record<string, string> }>>(
      `/policies/pending${merchantId ? `?merchantId=${merchantId}` : ''}`),
};
